import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { tournamentsApi } from '../services/api';
import { Tournament, TournamentRegistration, TournamentRegistrationStatus } from '../types';

const Tournaments: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [following, setFollowing] = useState<Set<string>>(new Set());
  const [registrations, setRegistrations] = useState<Map<string, TournamentRegistration>>(new Map());
  const [registering, setRegistering] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadTournaments();
    loadFollowedTournaments();
    loadRegistrations();
  }, []);

  const loadTournaments = async () => {
    try {
      setLoading(true);
      const response = await tournamentsApi.getAll();
      setTournaments(response.data.filter(t => t.is_active));
    } catch (error) {
      console.error('Failed to load tournaments:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadFollowedTournaments = async () => {
    try {
      const response = await tournamentsApi.getFollowed();
      const followedIds = new Set(response.data.map(t => t.id));
      setFollowing(followedIds);
    } catch (error) {
      console.error('Failed to load followed tournaments:', error);
    }
  };

  const loadRegistrations = async () => {
    try {
      const response = await tournamentsApi.getRegistrations();
      const registrationsMap = new Map<string, TournamentRegistration>();
      response.data.forEach(reg => {
        registrationsMap.set(reg.tournament_id, reg);
      });
      setRegistrations(registrationsMap);
    } catch (error) {
      console.error('Failed to load registrations:', error);
    }
  };

  const handleRegister = async (tournamentId: string) => {
    try {
      setRegistering(prev => new Set(prev).add(tournamentId));
      const response = await tournamentsApi.register(tournamentId);
      setRegistrations(prev => {
        const newMap = new Map(prev);
        newMap.set(tournamentId, response.data);
        return newMap;
      });
      alert('Registration successful!');
    } catch (error: any) {
      console.error('Failed to register:', error);
      alert(error.response?.data?.detail || 'Failed to register. Please try again.');
    } finally {
      setRegistering(prev => {
        const newSet = new Set(prev);
        newSet.delete(tournamentId);
        return newSet;
      });
    }
  };

  const getRegistrationStatusBadge = (status: TournamentRegistrationStatus) => {
    const statusConfig = {
      PENDING: { color: 'bg-yellow-100 text-yellow-800', text: 'Pending' },
      ACCEPTED: { color: 'bg-green-100 text-green-800', text: 'Accepted' },
      REJECTED: { color: 'bg-red-100 text-red-800', text: 'Rejected' },
    };
    const config = statusConfig[status];
    return (
      <span className={`px-2 py-1 text-xs rounded ${config.color}`}>
        {config.text}
      </span>
    );
  };

  const handleFollow = async (tournamentId: string) => {
    try {
      await tournamentsApi.follow(tournamentId);
      setFollowing(prev => {
        const newSet = new Set(prev);
        newSet.add(tournamentId);
        return newSet;
      });
    } catch (error: any) {
      console.error('Failed to follow tournament:', error);
      // Handle "Already following this tournament" error gracefully
      if (error.response?.status === 400 && error.response?.data?.detail === "Already following this tournament") {
        // User is already following, just update the UI state
        setFollowing(prev => {
          const newSet = new Set(prev);
          newSet.add(tournamentId);
          return newSet;
        });
      } else {
        alert('Failed to follow tournament. Please try again.');
      }
    }
  };

  const handleUnfollow = async (tournamentId: string) => {
    try {
      await tournamentsApi.unfollow(tournamentId);
      setFollowing(prev => {
        const newSet = new Set(prev);
        newSet.delete(tournamentId);
        return newSet;
      });
    } catch (error) {
      console.error('Failed to unfollow tournament:', error);
      alert('Failed to unfollow tournament. Please try again.');
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex justify-center items-center h-64">
          <div className="text-lg text-gray-600">Loading tournaments...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold text-gray-900 mb-6">Active Tournaments</h1>
        
        {tournaments.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-600 text-lg">No active tournaments found.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {tournaments.map((tournament) => {
              const isFollowing = following.has(tournament.id);
              
              const registration = registrations.get(tournament.id);
              const isRegistering = registering.has(tournament.id);
              
              return (
                <div key={tournament.id} className="bg-white rounded-lg shadow-md p-6">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="text-xl font-semibold text-gray-800">{tournament.name}</h3>
                    {registration && getRegistrationStatusBadge(registration.status)}
                  </div>
                  <p className="text-gray-600 mb-4">{tournament.description}</p>
                  
                  <div className="space-y-2 mb-4">
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Date:</span>
                      <span className="text-sm font-medium">
                        {new Date(tournament.date).toLocaleDateString()}
                      </span>
                    </div>
                    
                    {tournament.circuit && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Circuit:</span>
                        <span className="text-sm font-medium">{tournament.circuit.name}</span>
                      </div>
                    )}
                    
                    {tournament.ruleset && (
                      <div className="flex justify-between">
                        <span className="text-sm text-gray-500">Budget:</span>
                        <span className="text-sm font-medium">
                          {tournament.ruleset.budget_limit 
                            ? `${tournament.ruleset.budget_currency === 'EUR' ? '€' : tournament.ruleset.budget_currency === 'USD' ? '$' : 'TIX'}${tournament.ruleset.budget_limit}`
                            : 'No Budget'
                          }
                        </span>
                      </div>
                    )}
                    
                    <div className="flex justify-between">
                      <span className="text-sm text-gray-500">Registration:</span>
                      <span className="text-sm font-medium">
                        {new Date(tournament.registration_open).toLocaleDateString()} - {new Date(tournament.registration_close).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  <div className="flex flex-wrap gap-2">
                    {!registration ? (
                      <button
                        onClick={() => handleRegister(tournament.id)}
                        disabled={isRegistering}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm disabled:opacity-50"
                      >
                        {isRegistering ? 'Registering...' : 'Register'}
                      </button>
                    ) : (
                      <div className="w-full mb-2">
                        <p className="text-sm text-gray-600">
                          Registration: {getRegistrationStatusBadge(registration.status)}
                        </p>
                      </div>
                    )}
                    
                    <button
                      onClick={() => isFollowing ? handleUnfollow(tournament.id) : handleFollow(tournament.id)}
                      className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                        isFollowing 
                          ? 'bg-red-600 text-white hover:bg-red-700' 
                          : 'bg-gray-600 text-white hover:bg-gray-700'
                      }`}
                    >
                      {isFollowing ? 'Unfollow' : 'Follow'}
                    </button>
                    
                    {registration && registration.status === 'ACCEPTED' && (
                      <Link
                        to={`/submit-deck?tournament=${tournament.id}`}
                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm"
                      >
                        Submit Deck
                      </Link>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Tournaments;